import React, { useState, useEffect, useRef } from "react";
import {
  Upload,
  Database,
  Save,
  RefreshCcw,
  CheckCircle,
  FileText,
  Trash2
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const KnowledgeGraph = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("connection");
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const [vectorConfig, setVectorConfig] = useState({
    strategy: 'RecursiveChunker',
    chunk_size: 2000,
    chunk_overlap: 200,
    search_top_k: 5,
    search_threshold: 0.5,
    search_type: 'hybrid',
    knowledge_source: 'vector',
    neo4j_uri: 'bolt://localhost:7687',
    neo4j_user: 'neo4j',
    neo4j_password: '',
    neo4j_database: 'neo4j',
    params: {}
  });

  useEffect(() => {
    loadDocuments();
    fetchVectorConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAiAgentId]);

  const loadDocuments = async () => {
    try {
      const response = await ApiClient.getDocuments();
      const docs = response.documents || [];
      const formattedDocs = docs
        .filter(doc => (doc.original_name || '').toLowerCase().endsWith('.cypher'))
        .map(doc => ({
          ...doc,
          name: doc.original_name,
          dbId: doc.id
        }));
      setDocuments(formattedDocs);
    } catch (error) {
      console.error("Failed to load Cypher documents:", error);
    }
  };

  const fetchVectorConfig = async () => {
    try {
      const response = await ApiClient.request('/api/vector/config');
      if (response) {
        setVectorConfig({
          ...response,
          params: response.params || {}
        });
      }
    } catch (error) {
      console.error('Error fetching Neo4j config:', error);
    }
  };

  const saveVectorConfig = async () => {
    setConfigSaving(true);
    try {
      await ApiClient.request('/api/vector/config', {
        method: 'POST',
        body: JSON.stringify(vectorConfig)
      });
      showToast("Knowledge source configuration updated successfully!", "success");
    } catch (error) {
      console.error('Error saving Neo4j config:', error);
      showToast("Failed to save configuration", "error");
    } finally {
      setConfigSaving(false);
    }
  };

  const handleFiles = async (files) => {
    const validFiles = files.filter(file => file.name.toLowerCase().endsWith('.cypher'));
    if (validFiles.length === 0) {
      showToast("Please upload valid .cypher files only", "error");
      return;
    }

    setUploading(true);
    try {
      const response = await ApiClient.uploadDocuments(validFiles, {});
      const uploadedFiles = response.files || [];
      showToast(`Uploaded ${validFiles.length} file(s). Executing queries...`, "info");
      
      uploadedFiles.forEach(file => {
        pollDocumentStatus(file.id, file.filename || file.original_name);
      });
      loadDocuments();
    } catch (error) {
      console.error("Upload failed:", error);
      showToast(error.message || "Failed to upload Cypher file", "error");
      setUploading(false);
    }
  };

  const pollDocumentStatus = async (docId, filename) => {
    const checkStatus = async () => {
      try {
        const statusResponse = await ApiClient.request(`/documents/status/${docId}`);
        if (statusResponse.status === "completed") {
          showToast(`Cypher file '${filename}' executed successfully!`, "success");
          await loadDocuments();
          setUploading(false);
        } else if (statusResponse.status === "failed") {
          showToast(`Cypher execution failed for '${filename}': ${statusResponse.status_detail || "Unknown error"}`, "error", 10000);
          await loadDocuments();
          setUploading(false);
        } else {
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error("Status polling failed:", error);
        setTimeout(checkStatus, 2000);
      }
    };
    setTimeout(checkStatus, 100);
  };

  const confirmDeleteDoc = (doc) => {
    setDocToDelete(doc);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!docToDelete) return;
    setIsDeleting(true);
    try {
      await ApiClient.deleteDocument(docToDelete.dbId);
      showToast("Cypher history record deleted", "success");
      loadDocuments();
    } catch (error) {
      console.error("Delete failed:", error);
      showToast("Failed to delete record", "error");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setDocToDelete(null);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-7xl mx-auto space-y-8 min-h-screen animate-fadeIn">
      <PageHeader 
        title="Knowledge Graph (Neo4j)" 
        description="Configure Graph Database connection parameters and upload Cypher files to execute graph structures."
        actions={
          <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
            <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${vectorConfig.knowledge_source === 'graph' ? "text-[#83bbe1]" : "text-gray-300"}`}>
              {vectorConfig.knowledge_source === 'graph' ? "Active" : "Inactive"}
            </span>

            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={vectorConfig.knowledge_source === 'graph'}
                onChange={async () => {
                  const newSource = vectorConfig.knowledge_source === 'graph' ? 'vector' : 'graph';
                  const updated = { ...vectorConfig, knowledge_source: newSource };
                  setVectorConfig(updated);
                  
                  setConfigSaving(true);
                  try {
                    await ApiClient.request('/api/vector/config', {
                      method: 'POST',
                      body: JSON.stringify(updated)
                    });
                    showToast(`Knowledge source updated to ${newSource === 'graph' ? 'Knowledge Graph (Neo4j)' : 'Vector Search (RAG)'}!`, "success");
                  } catch (e) {
                    showToast("Failed to update knowledge source", "error");
                  } finally {
                    setConfigSaving(false);
                  }
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
            </label>
          </div>
        }
      />

      <div className="flex gap-2 p-1.5 bg-gray-50 rounded-2xl w-fit mx-auto">
        <button
          onClick={() => setActiveTab("connection")}
          className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
            activeTab === "connection"
              ? "bg-gray-900 text-white shadow-lg"
              : "text-gray-400 hover:text-gray-900"
          }`}
        >
          <Database size={14} />
          Connection Settings
        </button>
        <button
          onClick={() => setActiveTab("ingest")}
          className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
            activeTab === "ingest"
              ? "bg-gray-900 text-white shadow-lg"
              : "text-gray-400 hover:text-gray-900"
          }`}
        >
          <Upload size={14} />
          Cypher Ingestion
        </button>
      </div>

      {activeTab === "connection" ? (
        <div className="bg-white border border-gray-100 rounded-[40px] p-10 lg:p-14 shadow-xl shadow-gray-100/50 space-y-8 animate-slideIn">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
              <Database size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest tracking-tight">Database Credentials</h3>
              <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Specify Neo4j connection parameters</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block">
                  Neo4j URI
                </label>
                <input
                  type="text"
                  placeholder="bolt://localhost:7687"
                  value={vectorConfig.neo4j_uri || ""}
                  onChange={(e) => setVectorConfig({ ...vectorConfig, neo4j_uri: e.target.value })}
                  className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-[11px] transition-all"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block">
                    Neo4j Username
                  </label>
                  <input
                    type="text"
                    placeholder="neo4j"
                    value={vectorConfig.neo4j_user || ""}
                    onChange={(e) => setVectorConfig({ ...vectorConfig, neo4j_user: e.target.value })}
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-[11px] transition-all"
                  />
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block">
                    Neo4j Database
                  </label>
                  <input
                    type="text"
                    placeholder="neo4j"
                    value={vectorConfig.neo4j_database || ""}
                    onChange={(e) => setVectorConfig({ ...vectorConfig, neo4j_database: e.target.value })}
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-[11px] transition-all"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block">
                  Neo4j Password
                </label>
                <input
                  type="password"
                  placeholder="Enter Neo4j Password"
                  value={vectorConfig.neo4j_password || ""}
                  onChange={(e) => setVectorConfig({ ...vectorConfig, neo4j_password: e.target.value })}
                  className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-[11px] transition-all"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={saveVectorConfig}
              disabled={configSaving}
              className="flex items-center gap-2 px-10 py-3 bg-gray-900 text-white rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-gray-800 transition-all shadow-xl active:scale-95 disabled:opacity-40"
            >
              {configSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {configSaving ? 'Deploying...' : 'Deploy Settings'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-8 animate-slideIn">
          {/* Upload Area */}
          <div
            className={`bg-white rounded-[32px] p-8 border-2 border-dashed transition-all duration-300 relative overflow-hidden group ${
              isDragging
                ? "border-indigo-600 bg-indigo-50/10 ring-4 ring-indigo-600/5"
                : "border-gray-100 bg-white hover:border-gray-300"
            } cursor-pointer shadow-xl shadow-gray-100/50`}
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFiles(Array.from(e.dataTransfer.files)); }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => handleFiles(Array.from(e.target.files))}
              className="hidden"
              accept=".cypher"
            />
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-[20px] border border-gray-100 flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300 shadow-sm">
                <Upload className="w-8 h-8 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-1.5 tracking-tight uppercase">
                {isDragging ? "Drop Cypher File" : "Ingest Cypher File"}
              </h3>
              <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1 max-w-xs leading-relaxed">
                Drag and drop .cypher files or click to browse
              </p>
              <div className="flex flex-wrap justify-center gap-1.5 mt-4">
                <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[9px] font-bold tracking-wider">.CYPHER</span>
              </div>

              {uploading && (
                <div className="mt-6 flex items-center gap-2 px-5 py-2 bg-gray-900 text-white rounded-xl shadow-xl animate-bounce">
                  <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  <span className="font-bold text-[9px] uppercase tracking-widest animate-pulse">Running Cypher Queries...</span>
                </div>
              )}
            </div>
          </div>

          {/* History list */}
          {documents.length > 0 && (
            <div className="bg-white rounded-[28px] p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-6 px-1">
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Ingestion History</h3>
                <span className="bg-gray-100 text-gray-400 px-3 py-1 rounded-md text-[8px] font-black uppercase tracking-widest">
                  {documents.length} Cypher Files
                </span>
              </div>
              <div className="grid gap-2 mb-4 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                {documents.map((doc, index) => (
                  <div key={doc.dbId} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-gray-50 hover:border-gray-200 transition-all">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-indigo-50/50 flex items-center justify-center text-indigo-600 shrink-0">
                        <FileText size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-black text-gray-900 truncate leading-tight uppercase tracking-wider">{doc.name}</p>
                        <p className="text-[8px] text-gray-400 mt-0.5">Executed Successfully</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="flex items-center gap-1 text-[8px] font-black text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md uppercase tracking-widest">
                        <CheckCircle size={10} /> Active
                      </span>
                      <button
                        onClick={() => confirmDeleteDoc(doc)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Cypher Record"
        message="Are you sure you want to delete this Cypher ingestion record? This will not undo database changes but removes the file record."
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default KnowledgeGraph;
